import { createAsyncThunk } from '@reduxjs/toolkit';
import { getStockMovementsRequest, getStockRequest } from './stockApi';
import { STOCK_GROUP_BY } from './stockConstants';
import { selectCurrentWarehouseId } from '../warehouseContext/warehouseContextSlice';
import { extractApiError } from '../../utils/errors';

/** The product, narrowed to the warehouse in use when there is one. */
const scopeOf = (state, productId) => {
  const params = { product: productId };
  const warehouseId = selectCurrentWarehouseId(state);
  if (warehouseId) params.warehouse = warehouseId;
  return params;
};

/**
 * Who the product is and how much of it there is: the header of its kardex.
 *
 * Asked from the balances, consolidated, so the three figures are the same ones
 * the listing showed. Zeros included — a product that ran out still has a
 * history, and that is usually why somebody is looking at it.
 *
 * @param {number|string} productId
 * @returns {Promise<{product: object|null, totals: object}>}
 */
export const fetchKardexSummary = createAsyncThunk(
  'stockKardex/fetchKardexSummary',
  async (productId, { getState, rejectWithValue }) => {
    try {
      const response = await getStockRequest({
        ...scopeOf(getState(), productId),
        group_by: STOCK_GROUP_BY.SKU,
        include_zero: '1',
        page_size: 1,
      });

      return {
        product: response.data?.results?.[0] || null,
        totals: response.data?.totals || {},
      };
    } catch (error) {
      console.error('Error fetching kardex summary:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo obtener el producto'));
    }
  }
);

/**
 * The movements that explain a balance, newest first.
 *
 * @param {number|string} productId
 * @returns {Promise<{count: number, results: Array<object>}>}
 */
export const fetchKardexMovements = createAsyncThunk(
  'stockKardex/fetchKardexMovements',
  async (productId, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { page, rowsPerPage } = state.stockKardex.filters;

      const response = await getStockMovementsRequest({
        ...scopeOf(state, productId),
        page,
        page_size: rowsPerPage,
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching kardex movements:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo obtener el kardex'));
    }
  }
);
